import { ReactElement } from 'react';
import { Box } from '@twilio-paste/core/box';
import { Heading } from '@twilio-paste/core/heading';
import { SystemStatusIcon } from '@twilio-paste/icons/esm/SystemStatusIcon';
import { LinkExternalIcon } from '@twilio-paste/icons/esm/LinkExternalIcon';
import {
  Popover,
  PopoverContainer,
  PopoverButton,
} from '@twilio-paste/core/popover';
import { Button } from '@twilio-paste/core/button';
import { IconSize } from '@twilio-paste/core/style-props';

import { Paragraph } from '@twilio-paste/core/paragraph';
import { Text } from '@twilio-paste/core/text';

import { ProcessErrorIcon } from '@twilio-paste/icons/esm/ProcessErrorIcon';
import { ProcessWarningIcon } from '@twilio-paste/icons/esm/ProcessWarningIcon';
import { ProcessSuccessIcon } from '@twilio-paste/icons/esm/ProcessSuccessIcon';
import { ProductSegmentIcon } from '@twilio-paste/icons/esm/ProductSegmentIcon';
import { ProductEmailAPIIcon } from '@twilio-paste/icons/esm/ProductEmailAPIIcon';
import { systemStatusTypes } from '@twiliointernal/services-common/components/systemStatus';
import { getConfig } from '~/org/twilio/one-support-center/shared/osc-env';

import ErrorMenu from './errorMenu';
import { constants, copy, types } from '../definitions';
import { useSegment, useSystemStatus } from '../../../hooks';
import { trackingDetails } from '../../../definitions/constants';
import logger from '../../../../lib/logger';
import zipwhipLogoGrey from '../../../../assets/zipwhip-logo-grey.svg';

import styles from '../layout.module.css';
import { dataTestIds } from '@twiliointernal/org/twilio/e2-commons/shared/definitions';

const { incidentStatusPage } = getConfig();

const statusToIconMap = {
  [systemStatusTypes.SystemStatusTypes.error]: (): ReactElement => (
    <ProcessErrorIcon color="colorTextIconError" decorative />
  ),
  [systemStatusTypes.SystemStatusTypes.success]: (): ReactElement => (
    <ProcessSuccessIcon color="colorTextIconSuccess" decorative />
  ),
  [systemStatusTypes.SystemStatusTypes.warning]: (): ReactElement => (
    <ProcessWarningIcon color="colorTextIconWarning" decorative />
  ),
};

const SystemStatusMenuIcon = (size: IconSize): ReactElement => {
  const { trackEvent } = useSegment();
  const { systemStatuses, statusPageUrl, loading, hasError } =
    useSystemStatus();

  if (!systemStatuses || loading) {
    return <div />;
  }

  if (hasError) {
    return (
      <ErrorMenu
        type={types.ErrorMenuType.systemStatus}
        redirectUrl={statusPageUrl}
      />
    );
  }

  const groupedStatus: types.StatusByGroup = systemStatuses.reduce(
    (accumulator, systemStatus) => ({
      ...accumulator,
      [systemStatus.statusType]: [
        ...accumulator[systemStatus.statusType],
        systemStatus,
      ],
    }),
    {
      [systemStatusTypes.SystemStatusTypes.success]: [],
      [systemStatusTypes.SystemStatusTypes.warning]: [],
      [systemStatusTypes.SystemStatusTypes.error]: [],
    },
  );

  const renderStatus = ({
    name,
    statusType,
  }: systemStatusTypes.SytemStatus): ReactElement => {
    const renderIconComponent = statusToIconMap[statusType];

    return (
      <Box as="div" key={name} className={styles.statusListItem}>
        {renderIconComponent()}

        <Text as="p" fontSize="fontSize30" marginLeft="space30">
          {name}
        </Text>
      </Box>
    );
  };

  const renderStatusGroup = (
    type: systemStatusTypes.SystemStatusTypes,
    group: systemStatusTypes.SytemStatus[],
  ): ReactElement => {
    return (
      <Box as="div" className={styles.statusList}>
        <Paragraph marginBottom="space0">
          {copy.topbar.systemStatus.statusHeadings[type]}
        </Paragraph>

        {group.map((successStatus) => renderStatus(successStatus))}
      </Box>
    );
  };

  const handleDetailedStatusesClick = (): void => {
    window.open(statusPageUrl, '_blank');
  };

  const handleOtherSystemStatusClick = (
    brand: string,
    brandUrl: string,
  ): void => {
    const eventName = trackingDetails.statusPageLinkClick;

    logger.info(
      `${constants.logPrefixes.systemStatus}::[${brand} ${eventName}]`,
      {
        brand,
      },
    );
    trackEvent(eventName, {
      brand,
    });
    window.open(brandUrl, '_blank');
  };

  const hasSystemErrors =
    groupedStatus?.error.length || groupedStatus?.warning.length;
  const menuHeading = hasSystemErrors
    ? copy.topbar.systemStatus.headingError(
        groupedStatus.warning.length,
        groupedStatus.error.length,
      )
    : copy.topbar.systemStatus.headingSuccess;

  return (
    <Box as="span" className={styles.topbarMenusContainer}>
      <PopoverContainer baseId="system-status-popover">
        <PopoverButton
          variant="secondary_icon"
          size="icon_small"
          data-testid={dataTestIds.systemStatusButton}
        >
          {hasSystemErrors && (
            <SystemStatusIcon
              decorative={false}
              title="Open System Status view"
              size={size}
            />
          )}
          {!hasSystemErrors && (
            <SystemStatusIcon
              decorative={false}
              title="Open System Status view"
              size={size}
            />
          )}
          <Text
            as="span"
            color="colorTextIcon"
            fontSize="fontSize30"
            fontWeight="fontWeightLight"
          >
            {copy.topbar.systemStatusButton}
          </Text>
        </PopoverButton>
        <Popover aria-label="Popover">
          <Heading variant="heading20" as="h1">
            {copy.systemStatuses.heading}
          </Heading>
          <Box as="div" className={styles.topbarMenuDetailsContainer}>
            <Heading variant="heading40" as="h3">
              {copy.systemStatuses.twilioStatusHeading}
            </Heading>
            <Heading variant="heading50" as="h3">
              {menuHeading}
            </Heading>

            <Box as="div" className={styles.topbarMenuFeaturesContainer}>
              <Box as="div" className={styles.statusDetailsContainer}>
                {hasSystemErrors && (
                  <Box as="div" className={styles.statusErrorContainer}>
                    {!!groupedStatus.warning.length &&
                      renderStatusGroup(
                        systemStatusTypes.SystemStatusTypes.warning,
                        groupedStatus.warning,
                      )}

                    {!!groupedStatus.error.length &&
                      renderStatusGroup(
                        systemStatusTypes.SystemStatusTypes.error,
                        groupedStatus.error,
                      )}
                  </Box>
                )}
                {renderStatusGroup(
                  systemStatusTypes.SystemStatusTypes.success,
                  groupedStatus.success,
                )}
              </Box>
            </Box>
            <Button
              variant="secondary"
              onClick={(): void => handleDetailedStatusesClick()}
            >
              {copy.topbar.systemStatus.ctaValue}
              <LinkExternalIcon
                decorative={false}
                title="Description of icon"
              />
            </Button>
          </Box>
          <Heading variant="heading30" as="h2">
            {copy.otherSystemStatuses.heading}
          </Heading>
          <Box as="div" className={styles.otherSystemStatusDetailsContainer}>
            <Button
              variant="secondary"
              onClick={(): void =>
                handleOtherSystemStatusClick(
                  copy.otherSystemStatuses.iconTitles.segment,
                  incidentStatusPage.segment,
                )
              }
            >
              <ProductSegmentIcon
                decorative
                color="colorTextIcon"
                title={copy.otherSystemStatuses.iconTitles.segment}
              />
              {copy.otherSystemStatuses.segment}
            </Button>

            <Button
              variant="secondary"
              onClick={(): void =>
                handleOtherSystemStatusClick(
                  copy.otherSystemStatuses.iconTitles.sendGrid,
                  incidentStatusPage.sendGrid,
                )
              }
            >
              <ProductEmailAPIIcon
                decorative
                color="colorTextIcon"
                title={copy.otherSystemStatuses.iconTitles.sendGrid}
              />
              {copy.otherSystemStatuses.sendGrid}
            </Button>

            <Button
              variant="secondary"
              onClick={(): void =>
                handleOtherSystemStatusClick(
                  copy.otherSystemStatuses.iconTitles.zipwhip,
                  incidentStatusPage.zipwhip,
                )
              }
            >
              <Box
                as="img"
                src={zipwhipLogoGrey}
                height="22px"
                alt="Zipwhip logo"
              />
              {copy.otherSystemStatuses.zipwhip}
            </Button>
          </Box>
        </Popover>
      </PopoverContainer>
    </Box>
  );
};

export default SystemStatusMenuIcon;
